import React from 'react';
import { Badge } from '@/components/ui/badge';
import { HardHat } from 'lucide-react';

const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString()}`);
const pct = (v) => (v == null ? '—' : `${v}%`);
const Item = ({ label, value, testid }) => <div data-testid={testid}><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-medium">{value || value === 0 ? value : '—'}</p></div>;

// Read-only "Construction Loan Details" on the lead details tab, incl. per-scenario computed metrics.
export const ConstructionDetailsView = ({ construction: c }) => {
  if (!c) return null;
  const a = c.address || {};
  const addr = [a.street, a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return (
    <div className="border rounded-lg p-4 space-y-4" data-testid="construction-details-view">
      <h3 className="font-semibold flex items-center gap-2"><HardHat className="h-4 w-4" /> Construction Loan Details</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Item label="Borrower" value={c.borrower_full_name} />
        <Item label="Loan program" value={c.loan_program} testid="constr-view-loan_program" />
        <Item label="Occupancy" value={c.occupancy} />
        <Item label="Entity" value={[c.borrowing_entity, c.entity_name].filter(Boolean).join(' — ')} />
        <Item label="Guarantor FICO" value={c.fico_score} />
        <Item label="Liquidity" value={c.liquidity != null ? money(c.liquidity) : null} />
        <Item label="Completed projects" value={c.experience_projects != null ? `${c.experience_projects}${c.experience_notes ? ` — ${c.experience_notes}` : ''}` : c.experience_notes} />
        <Item label="Exit strategy" value={c.exit_strategy} />
        <div className="col-span-2"><Item label="Property address" value={addr} testid="constr-view-address" /></div>
        <Item label="Under contract" value={c.under_contract == null ? null : c.under_contract ? `Yes${c.contract_closing_date ? ` — closes ${c.contract_closing_date}` : ''}` : 'No'} />
        <Item label="Plans / permits" value={[c.plans_status && `Plans: ${c.plans_status}`, c.permits_status && `Permits: ${c.permits_status}`].filter(Boolean).join(' · ')} />
      </div>
      {c.project_notes && <p className="text-sm"><span className="text-muted-foreground text-xs">Project notes: </span>{c.project_notes}</p>}
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Development scenarios</p>
        {(c.scenarios || []).map((s, i) => {
          const m = s.metrics || {};
          return (
            <div key={s.scenario_id || i} className="border rounded p-3 text-sm space-y-1" data-testid={`constr-view-scenario-${i}`}>
              <p className="font-medium flex items-center gap-2">{s.name} {s.scenario_id === c.active_scenario_id && <Badge variant="outline" className="text-[10px]">used for matching</Badge>}</p>
              <p className="text-xs text-muted-foreground">
                {s.property_type || 'type —'} · {s.lots ?? '—'} lot(s) · {s.units ?? '—'} unit(s) · {s.timeline_months ? `${s.timeline_months} mo` : 'timeline —'}
              </p>
              <p className="text-xs">
                Acquisition {money(s.total_acquisition_price)}{s.acquisition_auto_calculated ? ' (auto: lots × price/lot)' : ''} · Budget {money(s.construction_budget)} · <b>Total cost {money(m.total_project_cost)}</b> · ARV {money(m.arv_low)}{m.arv_high && m.arv_high !== m.arv_low ? `–${money(m.arv_high)}` : ''} · Loan {money(s.loan_amount_requested)}
              </p>
              <p className="text-xs" data-testid={`constr-view-scenario-${i}-metrics`}>
                LTC <b>{pct(m.ltc)}</b>{m.ltc == null && ' (need acquisition, budget & loan)'} · LTARV <b>{pct(m.ltarv)}</b>{m.ltarv_best_case != null && m.ltarv_best_case !== m.ltarv ? ` (best case ${pct(m.ltarv_best_case)})` : ''}{m.ltarv == null && ' (need ARV & loan)'}
              </p>
              {s.notes && <p className="text-xs text-muted-foreground">{s.notes}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConstructionDetailsView;
